import { customElement, property, state } from 'lit/decorators.js';
import { CSSResult, html, PropertyValues, TemplateResult, unsafeCSS } from 'lit';
import { consume } from '@lit/context';
import styles from './product-action-menu.scss?inline';
import { ProductIdentifierContainer } from '../../../@types/wishlist.js';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import type { ActionMenuProductInfo } from '../action-menu-product-info/action-menu-product-info.js';
import '../action-menu-product-info/action-menu-product-info.js';
import { trackClickAndReserveEvent, trackProductActionMenuOpen } from '../../../scripts/service/cube-tracking-layer.js';
import { sizeLayerContext, SizeLayerState } from '../../../scripts/context/sizeLayerContext.js';
import { productFamilyContext, ProductFamilyState } from '../../../scripts/context/productFamilyContext.js';
import { buildPdsUrl } from '../../../scripts/service/build-pds-url.js';
import { ActionItem, ActionMenu } from '../action-menu/action-menu.js';
import { dispatchVspOpen } from '../vsp-modal/vsp-event.js';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';
import { productsContext } from '../../../scripts/context/wishlistContext.js';
import { ProductIdentifier } from '../../../scripts/service/product-identifier.js';
import { addToListLayerContext, AddToListLayerState } from '../../../scripts/context/addToListLayerContext.js';
import { ProductsState } from '../../../scripts/context/wishlist-state.js';
import { productMenuContext } from '../../../scripts/context/menuContext.js';
import { ProductMenuState } from '../../../scripts/context/menu-state.js';
import baseLayerStyles from '../base-layer/base-layer.scss?inline';

@customElement('ents-product-action-menu')
export class ProductActionMenu extends ActionMenu {
  protected testId: string = 'product-action-menu-layer';

  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @property({ type: Boolean, attribute: 'curated-collection', reflect: true })
  private isCuratedCollection: boolean = false;

  @consume({ context: productMenuContext, subscribe: true })
  @property({ attribute: false })
  private productMenuState!: ProductMenuState;

  @consume({ context: productsContext, subscribe: true })
  @property({ attribute: false })
  private productsState!: ProductsState;

  @consume({ context: sizeLayerContext })
  private sizeLayerState!: SizeLayerState;

  @consume({ context: productFamilyContext })
  private productFamilyState!: ProductFamilyState;

  @consume({ context: addToListLayerContext })
  private addToListLayerState!: AddToListLayerState;

  @state()
  private shownImage: HTMLImageElement | null = null;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles), unsafeCSS(baseLayerStyles)];
  }

  protected renderBanners(): TemplateResult {
    return html`
      <div class="ents-action-menu__product-banner">
        <slot name="product-banner"></slot>
      </div>
      <div class="ents-action-menu__gwp-banner">
        <slot name="gwp-banner"></slot>
      </div>
    `;
  }

  private resetProductBanner() {
    // workaround to hide the product banner, otherwise the banner could be visible for wrong products
    // (edge case, only if user quickly switches between action menus and internet is slow)
    // not a 100% fix, because with very slow internet the problem can still happen
    // CAMP is discussing a better alternative than sending '-1' as syan
    document.dispatchEvent(new CustomEvent('bewe:ents:variantenwechsel', { detail: { syan: -1 } })); // shared event used by other teams (e.g. BRTN, CAMP, KAUF)
    document.dispatchEvent(new CustomEvent('ents:wishlist:variant:changed', { detail: { syan: -1 } })); // Translation
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);

    const previousProductMenuState: ProductMenuState | undefined = _changedProperties.get('productMenuState');
    if (
      previousProductMenuState != null &&
      previousProductMenuState.context.activeMenu !== 'default' &&
      this.productMenuState.context.activeMenu === 'default' &&
      this.productMenuState.context.product
    ) {
      if (previousProductMenuState.context.activeMenu === null) {
        this.fetchAndSaveProductFamilyInfo();
        this.changeGWPBanner();
        this.trackOpenEvent();
      }
      this.showLayer = true;
      this.waitForImageElement();
    }
  }

  private async waitForImageElement() {
    await this.updateComplete;

    const productInfo = this.shadowRoot?.querySelector('ents-action-menu-product-info') as ActionMenuProductInfo | null;

    if (productInfo) {
      await productInfo.updateComplete;
      this.shownImage = productInfo.getShownImage();
    }
  }

  protected updateStateOnClose() {
    if (this.productMenuState.context.activeMenu === 'default') {
      this.productMenuState.closeMenu();
      this.resetProductBanner();
    }
  }

  private fetchAndSaveProductFamilyInfo() {
    let fetchUrl: string;
    let displayType: 'productline' | 'capsule' | 'mixandmatch' | undefined = undefined;
    const articleIdWithProductFamilySet = this.identifyArticleIdWithProductFamily();
    if (this.isProductLineEnabled() && articleIdWithProductFamilySet !== undefined) {
      fetchUrl = `${this.wishlistAppConfig.productLineEndpoint}${articleIdWithProductFamilySet}/`;
      displayType = 'productline';
    } else if (this.isCapsuleEnabled() && articleIdWithProductFamilySet !== undefined) {
      fetchUrl = `${this.wishlistAppConfig.capsuleEndpoint}${articleIdWithProductFamilySet}/`;
      displayType = 'capsule';
    } else if (this.isMixAndMatchEnabled() && articleIdWithProductFamilySet !== undefined) {
      fetchUrl = `${this.wishlistAppConfig.mixAndMatchEndpoint}${articleIdWithProductFamilySet}/`;
      displayType = 'mixandmatch';
    } else {
      return;
    }

    fetchUrl += '?request-from=wishlist';
    fetch(fetchUrl)
      .then((response) => (response.status === 200 ? response.json() : undefined))
      .then((json) => {
        this.productFamilyState.update({
          sliderData: json,
          displayType: displayType,
          beauty: this.productMenuState.context.product?.isBeauty,
        });
      });
  }

  private identifyArticleIdWithProductFamily() {
    if (this.productMenuState.context.product?.articleId) {
      return this.productMenuState.context.product.articleId;
    }
    // in case no size is selected yet, find first mm/line article
    return this.productMenuState.context.product?.articles.find(
      (art) =>
        (art.mixAndMatchMessage !== undefined && art.mixAndMatchMessage !== '') || art.productLine || art.capsule,
    )?.id;
  }

  protected renderInformation() {
    return html` <ents-action-menu-product-info></ents-action-menu-product-info>`;
  }

  protected getApplicableActionItems() {
    return [
      ...this.getSelectSizeItem(),
      ...(this.isCuratedCollection ? [] : this.getWvbItem()),
      ...(this.isCuratedCollection ? [] : this.getProductFamilyItem()),
      ...(this.isCuratedCollection ? [] : this.getVspItem()),
      ...(this.isCuratedCollection ? [] : this.getHvaItem()),
      ...(this.isCuratedCollection ? [] : this.getAddToListItem()),
      ...this.getShareItem(),
      ...(this.isCuratedCollection ? [] : this.getRemoveItem()),
    ];
  }

  private getSelectSizeItem(): ActionItem[] {
    if (
      this.productMenuState.context.product?.hasSizeOrQuantitySelection &&
      this.productMenuState.context.product?.articles.length > 0 &&
      this.productMenuState.context.product?.isNotOneSize
    ) {
      return [
        {
          label: this.messages['ents_wishlist_actionmenu_selectSize']!,
          arrow: true,
          trackingKey: 'size_selection',
          callback: () => {
            this.sizeLayerState.update({
              product: this.productMenuState.context.product,
              trackingContext: 'wishlist_action_menu',
              triggerA2C: false,
              open: true,
              selectedArticle: this.productMenuState.context.selectedArticle,
              backButtonCallback: this.open.bind(this),
            });
          },
        },
      ];
    }
    return [];
  }

  private getWvbItem(): ActionItem[] {
    const isOutOfStock =
      this.productMenuState.context.selectedArticle === null
        ? this.productMenuState.context.product?.isOutOfStock
        : this.productMenuState.context.selectedArticle.isOutOfStock;
    const isUnavailable = !this.productMenuState.context.product?.price;
    if (isOutOfStock && !isUnavailable && this.wishlistAppConfig.componentsVisibleWhenOutOfStock) {
      return [
        {
          label: this.messages['ents_wishlist_actionmenu_wvb']!,
          arrow: true,
          trackingKey: 'wvb',
          callback: () => {
            if (this.productMenuState.context.selectedArticle !== null) {
              const payload = {
                vertriebsinfoId: this.productMenuState.context.selectedArticle.salesInformationId,
                source: 'wishlist_action_menu',
              };
              document.dispatchEvent(new CustomEvent('pruefen:best:wvb-overlay:open', { detail: payload }));
              document.dispatchEvent(
                new CustomEvent<ProductIdentifierContainer>('ents:cta-event-emitted', {
                  detail: {
                    identifier: ProductIdentifier.createByProduct(this.productMenuState.context.product).id,
                  },
                }),
              );
              this.setAttribute('data-test-ents-notify-me-payload-emitted', JSON.stringify(payload));
            } else {
              this.sizeLayerState.update({
                product: this.productMenuState.context.product,
                trackingContext: 'wishlist_action_menu',
                triggerA2C: false,
                open: true,
                selectedArticle: this.productMenuState.context.selectedArticle,
                backButtonCallback: this.open.bind(this),
              });
            }
          },
        },
      ];
    }
    return [];
  }

  private isMixAndMatchEnabled(): boolean {
    const mixAndMatchMessage = this.productMenuState.context.product?.mixAndMatchMessage;
    return mixAndMatchMessage !== undefined && mixAndMatchMessage !== '';
  }

  private isProductLineEnabled(): boolean {
    const productLineExists = this.productMenuState.context.product?.productLine;
    return productLineExists !== undefined && productLineExists;
  }

  private isCapsuleEnabled(): boolean {
    const capsuleExists = this.productMenuState.context.product?.capsule;
    return capsuleExists !== undefined && capsuleExists;
  }

  private getProductFamilyItem(): ActionItem[] {
    if (this.isMixAndMatchEnabled()) {
      return [
        {
          label: this.messages['ents_wishlist_actionmenu_mixandmatch']!,
          arrow: true,
          trackingKey: 'mix&match',
          callback: () => {
            this.productFamilyState.update({
              open: true,
              backButtonCallback: this.open.bind(this),
            });
          },
        },
      ];
    }
    if (this.isCapsuleEnabled()) {
      return [
        {
          label: this.messages['ents_wishlist_actionmenu_capsule']!,
          arrow: true,
          trackingKey: 'capsule',
          callback: () => {
            this.productFamilyState.update({
              open: true,
              backButtonCallback: this.open.bind(this),
            });
          },
        },
      ];
    }
    if (this.isProductLineEnabled()) {
      return [
        {
          label: this.messages['ents_wishlist_actionmenu_productline']!,
          arrow: true,
          trackingKey: 'productline',
          callback: () => {
            this.productFamilyState.update({
              open: true,
              backButtonCallback: this.open.bind(this),
            });
          },
        },
      ];
    }
    return [];
  }

  private getHvaItem(): ActionItem[] {
    const product = this.productMenuState.context.product;
    const articles = product?.articles || [];
    const selectedArticle = this.productMenuState.context.selectedArticle;
    const currentArticle = selectedArticle ?? articles[0];
    const enabled =
      articles.length > 0 &&
      product?.isHvaButtonEnabled &&
      product?.hasStockInAnyStore &&
      !currentArticle.isMarketplace;
    if (enabled) {
      return [
        {
          label: this.messages['ents_wishlist_actionmenu_or']!,
          arrow: true,
          trackingKey: 'hva',
          callback: () => {
            const payload = { detail: { articleId: currentArticle.id, isSizeSelected: selectedArticle !== null } };
            document.dispatchEvent(new CustomEvent('ents:online-reservation:open', payload));
            this.setAttribute('data-test-ents-hva-payload-emitted', JSON.stringify(payload));
            trackClickAndReserveEvent();
          },
        },
      ];
    }
    return [];
  }

  private getVspItem(): ActionItem[] {
    if (this.productMenuState.context.product?.isVspButtonEnabled) {
      return [
        {
          label: this.messages['ents_wishlist_actionmenu_vsp']!,
          arrow: true,
          trackingKey: 'vsp',
          callback: () => {
            const product = this.productMenuState.context.product;
            if (product?.syan) {
              const payload = dispatchVspOpen(this.wishlistAppConfig?.recoVspEnabled ?? false, {
                syan: product.syan,
                colorId: product.colorId || undefined,
                listName: 'wishlist_actionmenu',
              });
              this.setAttribute('data-test-ents-vsp-payload-emitted', JSON.stringify(payload));
            }
          },
        },
      ];
    }
    return [];
  }

  private getShareItem(): ActionItem[] {
    const product = this.productMenuState.context.product;
    const itemInfoToShare = {
      url: buildPdsUrl(product?.pdsUrlBasePath, product?.articleId, product?.colorId),
      text: this.messages['ents_wishlist_sharing_article_body'],
    };
    if (product?.price !== undefined && navigator.share !== undefined && navigator.canShare(itemInfoToShare)) {
      return [
        {
          label: this.messages['ents_wishlist_actionmenu_shareItem']!,
          arrow: false,
          trackingKey: 'share',
          callback: () => {
            navigator.share(itemInfoToShare);
          },
        },
      ];
    }
    return [];
  }

  private getRemoveItem(): ActionItem[] {
    if (this.productsState.isShared()) {
      return [];
    }
    const product = this.productMenuState.context.product;
    const collection = this.productMenuState.context.collection;
    const labelText = collection
      ? this.messages['ents_wishlist_actionmenu_removeFromCollection']!
      : this.messages['ents_wishlist_actionmenu_removeItem']!;

    const callbackRemoveAction = collection
      ? () =>
          this.productMenuState.openMenu('remove-article', product!, {
            collection,
            backButtonCallback: this.open.bind(this),
          })
      : () => {
          const selectedArticle = this.productMenuState.context.selectedArticle;
          this.executeWishlistRemoveWithUndo(
            product!,
            selectedArticle,
            this.isOnAnyCollection(),
            this.shownImage?.currentSrc ?? '',
          );
        };

    return [
      {
        label: labelText,
        arrow: false,
        trackingKey: 'remove',
        callback: callbackRemoveAction,
      },
    ];
  }

  private isOnAnyCollection(): boolean {
    return this.productsState.isProductInAnyCollection(
      ProductIdentifier.createByProduct(this.productMenuState.context.product),
    );
  }

  private trackOpenEvent() {
    const items = this.getApplicableActionItems().map((item) => item.trackingKey);
    trackProductActionMenuOpen(
      items,
      this.productMenuState.context.product!.syan,
      this.productMenuState.context.selectedArticle,
      this.productMenuState.context.product?.colorId,
    );
  }

  private getAddToListItem(): ActionItem[] {
    return [
      {
        label: `${this.messages['ents_wishlist_addToList_add_to_list']}`,
        arrow: true,
        trackingKey: 'add_to_collection',
        callback: () => {
          this.addToListLayerState.update({
            open: true,
            backButtonCallback: this.open.bind(this),
          });
        },
      },
    ];
  }

  protected open() {
    this.productMenuState.openMenu('default', this.productMenuState.context.product!, {
      collection: this.productMenuState.context.collection,
      imageUrl: this.productMenuState.context.imageUrl,
      selectedArticle: this.productMenuState.context.selectedArticle,
    });
  }

  private changeGWPBanner() {
    const gwpbanner = document.querySelector('camp-gwpbanner');
    if (gwpbanner) {
      const { syan, colorId, articleId } = this.productMenuState.context.product || {};

      if (syan !== undefined) {
        gwpbanner.setAttribute('syan', syan.toString());
      }

      if (colorId !== undefined) {
        gwpbanner.setAttribute('farb-id', colorId);
      } else {
        gwpbanner.removeAttribute('farb-id');
      }

      if (articleId !== undefined) {
        gwpbanner.setAttribute('artikel-id', articleId);
      } else {
        gwpbanner.removeAttribute('artikel-id');
      }
    }
  }
}
